import { effect, onMount } from "@zyra/core";
import type { h as DOMFactory, HTMLAttributes, Props } from "@zyra/dom";

export type ButtonProps = HTMLAttributes<"button"> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
};
export type CardProps = HTMLAttributes<"section">;
export type BadgeProps = HTMLAttributes<"span"> & {
  variant?: "neutral" | "success" | "warning";
};
export type InputProps = HTMLAttributes<"input">;
export type TextareaProps = HTMLAttributes<"textarea">;
export type SelectProps = HTMLAttributes<"select">;
export type DialogProps = Omit<HTMLAttributes<"dialog">, "open"> & {
  open: boolean;
  /** Update your controlling signal when Escape, backdrop or a form closes the dialog. */
  onOpenChange: (open: boolean) => void;
  closeOnBackdrop?: boolean;
};

/** Preserve compiler getters so disabled, value, variants and children stay reactive. */
function forward(
  props: object,
  base: string,
  omit: string[] = [],
  defaults: Props = {},
): Props {
  const result: Props = { ...defaults };
  for (const name of Object.keys(props)) {
    if (name === "class" || name === "className" || omit.includes(name))
      continue;
    Object.defineProperty(result, name, {
      enumerable: true,
      configurable: true,
      get: () => (props as Props)[name],
    });
  }
  Object.defineProperty(result, "class", {
    enumerable: true,
    get: () =>
      [base, (props as Props).class, (props as Props).className]
        .filter(Boolean)
        .join(" "),
  });
  return result;
}

let modalLocks = 0;
let previousOverflow = "";
function lockPageScroll(): () => void {
  if (modalLocks++ === 0) {
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  let released = false;
  return () => {
    if (!released) {
      released = true;
      if (--modalLocks === 0) document.body.style.overflow = previousOverflow;
    }
  };
}

export function createUI(h: typeof DOMFactory) {
  function Button(props: ButtonProps) {
    const attrs = forward(props, "zy-button", ["variant", "size"], {
      type: "button",
    });
    Object.defineProperties(attrs, {
      // Safari does not focus pointer-clicked buttons by default. Establish the
      // trigger before opening overlays so focus restoration is consistent.
      onClick: {
        enumerable: true,
        configurable: true,
        value: (event: MouseEvent & { currentTarget: HTMLButtonElement }) => {
          event.currentTarget.focus({ preventScroll: true });
          props.onClick?.(event);
        },
      },
      "data-variant": {
        enumerable: true,
        get: () => props.variant ?? "primary",
      },
      "data-size": { enumerable: true, get: () => props.size ?? "md" },
    });
    return h("button", attrs);
  }
  function Card(props: CardProps) {
    return h("section", forward(props, "zy-card"));
  }
  function Badge(props: BadgeProps) {
    const attrs = forward(props, "zy-badge", ["variant"]);
    Object.defineProperty(attrs, "data-variant", {
      enumerable: true,
      get: () => props.variant ?? "neutral",
    });
    return h("span", attrs);
  }
  function Input(props: InputProps) {
    return h("input", forward(props, "zy-input"));
  }
  function Textarea(props: TextareaProps) {
    return h("textarea", forward(props, "zy-input zy-textarea"));
  }
  function Select(props: SelectProps) {
    return h("select", forward(props, "zy-input zy-select"));
  }
  function Dialog(props: DialogProps) {
    const attrs = forward(
      props,
      "zy-dialog",
      [
        "open",
        "onOpenChange",
        "closeOnBackdrop",
        "onCancel",
        "onClose",
        "onClick",
      ],
      { tabIndex: -1 },
    );
    const element = h("dialog", attrs) as HTMLDialogElement;
    // Native modal dialogs provide Escape handling, a top layer and focus containment.
    onMount(() => {
      let previous: HTMLElement | null = null;
      let unlock: (() => void) | undefined;
      const restore = () => {
        if (!unlock) return;
        unlock();
        unlock = undefined;
        if (previous?.isConnected) previous.focus();
      };
      const stop = effect(() => {
        if (props.open && !element.open) {
          previous = document.activeElement as HTMLElement | null;
          element.showModal();
          unlock = lockPageScroll();
        } else if (!props.open) {
          if (element.open) element.close();
          restore();
        }
      });
      const cancel = (event: Event) => {
        props.onCancel?.(event as Event & { currentTarget: HTMLDialogElement });
        if (!event.defaultPrevented) {
          event.preventDefault();
          props.onOpenChange(false);
        }
      };
      const close = (event: Event) => {
        props.onClose?.(event as Event & { currentTarget: HTMLDialogElement });
        if (props.open && !element.open) props.onOpenChange(false);
        if (!element.open) restore();
      };
      const click = (event: MouseEvent) => {
        props.onClick?.(
          event as MouseEvent & { currentTarget: HTMLDialogElement },
        );
        if (
          event.defaultPrevented ||
          props.closeOnBackdrop === false ||
          event.target !== element
        )
          return;
        const bounds = element.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          props.onOpenChange(false);
      };
      element.addEventListener("cancel", cancel);
      const keydown = (event: KeyboardEvent) => {
        if (event.defaultPrevented || event.key !== "Tab") return;
        const candidates = [
          ...element.querySelectorAll<HTMLElement>(
            "button, input, select, textarea, a[href], [tabindex]",
          ),
        ].filter(
          (node) =>
            node.tabIndex >= 0 &&
            !node.matches(":disabled, [hidden], [inert]") &&
            node.getClientRects().length > 0,
        );
        const first = candidates[0],
          last = candidates.at(-1);
        if (!first) {
          event.preventDefault();
          element.focus();
        } else if (
          event.shiftKey &&
          (document.activeElement === first ||
            document.activeElement === element)
        ) {
          event.preventDefault();
          last!.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      element.addEventListener("keydown", keydown);
      element.addEventListener("close", close);
      element.addEventListener("click", click);
      return () => {
        stop();
        element.removeEventListener("cancel", cancel);
        element.removeEventListener("close", close);
        element.removeEventListener("click", click);
        element.removeEventListener("keydown", keydown);
        if (element.open) {
          element.close();
        }
        restore();
      };
    });
    return element;
  }
  return { Button, Card, Badge, Input, Textarea, Select, Dialog };
}
